import { Component, EventEmitter, Input, Output } from '@angular/core';
import { EstimationWarning } from '../../models/estimation-v3.types';
import { EstimationWarningsService, WarningSeverity } from '../../services/estimation-warnings.service';

interface WarningGroup {
  severity: WarningSeverity;
  label: string;
  warnings: Array<{
    code: string;
    label: string;
    iconPath: string;
    details?: string;
  }>;
}

/**
 * Panel inline colapsable que muestra los warnings de una estimación v3
 * agrupados por severidad (blocking, risk, info).
 * Su apertura/cierre lo controla el host vía [expanded] y (closed).
 */
@Component({
  selector: 'app-estimation-warnings-panel',
  templateUrl: './estimation-warnings-panel.component.html',
  styleUrls: ['./estimation-warnings-panel.component.scss']
})
export class EstimationWarningsPanelComponent {
  @Input() set warnings(value: EstimationWarning[] | undefined | null) {
    this._warnings = value ?? [];
    this._groups = this.buildGroups(this._warnings);
  }
  get warnings(): EstimationWarning[] { return this._warnings; }

  @Input() expanded: boolean = false;

  @Output() closed = new EventEmitter<void>();

  private _warnings: EstimationWarning[] = [];
  private _groups: WarningGroup[] = [];

  constructor(private warningsService: EstimationWarningsService) {}

  get groups(): WarningGroup[] { return this._groups; }

  get totalCount(): number { return this._warnings.length; }

  get hasContent(): boolean { return this._warnings.length > 0; }

  onClose(): void {
    this.closed.emit();
  }

  trackByCode(_idx: number, w: { code: string }): string { return w.code; }

  private buildGroups(warnings: EstimationWarning[]): WarningGroup[] {
    if (!warnings || warnings.length === 0) return [];

    const order: WarningSeverity[] = ['blocking', 'risk', 'info'];
    const labels: Record<WarningSeverity, string> = {
      blocking: 'Bloqueantes',
      risk: 'Riesgos',
      info: 'Informativos'
    };

    const buckets: Record<WarningSeverity, WarningGroup['warnings']> = {
      blocking: [],
      risk: [],
      info: []
    };

    for (const w of warnings) {
      const severity = this.warningsService.getSeverity(w.code);
      buckets[severity].push({
        code: w.code,
        label: this.warningsService.getLabel(w.code),
        iconPath: this.warningsService.getIconPath(w.code),
        details: w.details
      });
    }

    return order
      .filter(sev => buckets[sev].length > 0)
      .map(sev => ({
        severity: sev,
        label: labels[sev],
        warnings: buckets[sev]
      }));
  }
}
